import {CleanHtml, emptyCleanHtml, htmlToText} from '@frontapp/editor-html';
import React, {Component, createRef, ForwardedRef, Key} from 'react';

import {AiSupportedLanguageCodesEnum} from '../../../../../../libs/shared/core/src/helpers/ai/constants';
import {sourceActivityIdAttribute} from '../../../../../../libs/shared/core/src/helpers/annotations/annotationHelpers';
import {
  containsSelection,
  isElement,
} from '../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {MessageAnnotationModel} from '../../../../../../libs/shared/core/src/models/api/annotationModel';
import {ChannelModel} from '../../../../../../libs/shared/core/src/models/api/channelModel';
import {ConversationActionContextsEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {
  MessageModel,
  MessageSourcesEnum,
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
  MessageVisualTypesEnum,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  InterfaceEventsEnum,
  ReplyLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  EmlPanelId,
  MessagePanelId,
  RViewerPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {KeyboardActionEventHandler} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {messageBodyHasExternalContent} from '../../../../../../srcCommon/inboxes/viewer/activity/message/messageViewerHelpers';
import {ReplyTypesEnum, VisualRecipientMap} from '../../../../../helpers/models/recipientHelpers';
import {ActivitySourceVisualTypesEnum} from '../../../../../models/api/activitySourceModel';
import {ConversationAiMarkModel, HARVESTING_MODEL} from '../../../../../models/api/conversationAiMarkModel';
import {IntegrationTypeModel} from '../../../../../models/api/integrationTypesModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {sanitize} from '../../../../../services/sanitize';
import {
  ExistingDraftError,
  ExistingDraftStrategiesEnum,
} from '../../../../../store/data/messages/draftMessagesActions';
import {EmlSource} from '../../../../../store/panels/eml/emlPanelState';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../common/annotation/annotationsHighlightViewer';
import {ViewerReplaceDraftDialog} from '../../dialogs/viewerReplaceDraftDialog';
import {BasicViewerResnoozeContainer} from '../basic/basicViewerResnoozeContainer';
import {CalendarInviteViewerContainer} from '../calendarInvite/calendarInviteViewerContainer';
import {IntegrationInformationBannerContainer} from '../integrationInformation/integrationInformationBannerContainer';
import {AiTagSuggestionBanner} from './banner/aiTagSuggestionBanner';
import {EditHereMessageBannerContainer} from './banner/editHereMessageBannerContainer';
import {FailedMessageBanner} from './banner/failedMessageBanner';
import {HiddenContentMessageBanner} from './banner/hiddenContentMessageBanner';
import {ImportMessageFailedBanner} from './banner/importMessageFailedBanner';
import {LaterMessageBanner} from './banner/laterMessageBanner';
import {MissingBodyMessageBanner} from './banner/missingBodyMessageBanner';
import {PendingDraftMessageBanner} from './banner/pendingDraftMessageBanner';
import {SharedDraftMessageBannerContainer} from './banner/sharedDraftMessageBannerContainer';
import {MessageViewerChat} from './chat/messageViewerChat';
import {MessageViewerEmail} from './email/messageViewerEmail';
import {MessageBase} from './messageBase';
import {MessageViewerBase} from './messageViewerBase';
import {MessageViewerCollapsed} from './messageViewerCollapsed';
import {CloneHandler} from './messageViewerMoreDropdown';
import {MessageViewerSynchronous} from './synchronous/messageViewerSynchronous';
import {MessageViewerTweetContainer} from './tweet/messageViewerTweetContainer';

/*
 * Types.
 */

export type ReplyHandler = (
  location: ReplyLocationsEnum,
  existingDraftStrategy: ExistingDraftStrategiesEnum,
) => void;
export type ForwardHandler = (
  location: ReplyLocationsEnum,
  shouldCreateConversation: boolean,
  existingDraftStrategy: ExistingDraftStrategiesEnum,
) => void;

/*
 * Constants.
 */

/**
 * Used to parse Front-inlined images from a message body when in the synchronous view.
 *
 * @deprecated Using regular expressions to manipulate HTML is a potential security risk.
 */
const bodyImgRE = /<div><img([\w\W]+?)><\/div>,?/g;

/*
 * Props.
 */

export interface MessageViewerWithoutConversationProps
  extends AnnotationsHighlightViewerInheritableProps<MessageAnnotationModel> {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
  cellsContext: CellsContext;
  namespace: string;
  activityId: number | undefined;
  activitySourceVisualType: ActivitySourceVisualTypesEnum | undefined;
  containsNewCalendarEvent?: boolean;
  message: MessageModel;
  isMessageShared: boolean;
  author?: TeammateModel;
  replyChannel?: ChannelModel;
  isLastMessage?: boolean;
  canceledReminder?: ReminderModel;
  visualRecipients: VisualRecipientMap;
  isExpanded: boolean;
  isChatUserOnline?: boolean;
  isUserVisitor: boolean;
  isUserAuthor: boolean;
  isUnread?: boolean;
  isVisible?: boolean;
  isNewUnread?: boolean;
  isFirstMessage?: boolean;
  isFirstActivity?: boolean;
  isOnlyMessage?: boolean;
  isOnlyActivity?: boolean;
  isAuthorReplying?: boolean;
  hasChannelToEdit?: boolean;
  isPreviousActivityMessage: boolean;
  isChatFollowUp: boolean;
  canHideExternalContent: boolean;
  canSplitMessage?: boolean;
  httpToken: string | undefined;
  defaultReplyType: ReplyTypesEnum;
  onExpandClick: () => void;
  onCollapseClick: () => void;
  onReplyClick: ReplyHandler;
  onReplyAllClick: ReplyHandler;
  onForwardClick: ForwardHandler;
  onCloneClick: CloneHandler;
  onDestroyConversationClick: () => void;
  onCancelSendClick: () => void;
  onStealDraftClick: () => void;
  onRetryFailedDraftClick: () => void;
  onDeleteDraftClick: () => void;
  onOpenMessageRenderClick: () => void;
  onPopoutClick: (arg: number | undefined) => void;
  relatedIntegration?: IntegrationTypeModel;
  emlSource?: EmlSource;
  isInMessagePopup: boolean;
  isInMessagePopout: boolean;
  jumpSourceActivityId?: number;
  annotations?: ReadonlyArray<MessageAnnotationModel>;
  aiTagMarks?: ReadonlyArray<ConversationAiMarkModel>;
  jumpKey?: Key;
  timelineVariation: TimelineVariationsEnum;
  isInbound?: boolean;
  scrollerVisibilityRef?: ForwardedRef<HTMLDivElement>;
  isReviewingAiTags?: boolean;
  lastSeenMessageUid?: string;
  hasMacroTriggers: boolean;
  preferredLanguages: ReadonlyArray<AiSupportedLanguageCodesEnum>;
  hasMessageTranslation: boolean;

  wasJustHighlighted?: boolean;
}

interface MessageViewerWithConversationProps extends MessageViewerWithoutConversationProps {
  // Both conversationId and conversationActionContext are needed to have the resnooze option available.
  conversationId: number;
  conversationActionContext: ConversationActionContextsEnum;

  // needed to display company name for some chat messages, depends on channel preferences
  widgetCompanyName?: string;
}

type MessageViewerProps = MessageViewerWithoutConversationProps | MessageViewerWithConversationProps;
function messageViewerPropsHasConversation(
  props: MessageViewerProps,
): props is MessageViewerWithConversationProps {
  return 'conversationId' in props && 'conversationActionContext' in props;
}

interface RenderContentProps extends MessageViewerWithoutConversationProps {
  onPopoutClick: () => void;
}

/*
 * Component.
 */

interface MessageViewerState {
  isExternalContentHidden: boolean;
  onConfirmReplaceDraft: (() => void) | undefined;
}

export class MessageViewer extends Component<MessageViewerProps, MessageViewerState> {
  constructor(props: MessageViewerProps) {
    super(props);
    this.state = {
      isExternalContentHidden: props.canHideExternalContent,
      onConfirmReplaceDraft: undefined,
    };
  }

  private messageBaseRef = createRef<HTMLDivElement>();

  onPopoutClick = () => {
    if (this.messageBaseRef.current) {
      this.props.onPopoutClick(this.messageBaseRef.current.clientHeight);
    }
  };

  // Used for handling the existing draft error for replies. This will first try
  // to execute the handler, and then catch ExistingDraftError's and show a warning
  // that the user is about to over-write their draft. If they confirm, the second handler
  // will then be called, which should set the existingDraftStrategy to replace
  private withConfirmReplaceDraft = (
    onReply: (arg: {existingDraftStrategy: ExistingDraftStrategiesEnum}) => void,
  ): void => {
    try {
      onReply({existingDraftStrategy: ExistingDraftStrategiesEnum.THROW_ERROR});
    } catch (error) {
      if (error instanceof ExistingDraftError) {
        this.setState({
          onConfirmReplaceDraft: () => onReply({existingDraftStrategy: ExistingDraftStrategiesEnum.REPLACE}),
        });
        return;
      }

      throw error;
    }
  };

  private onReplyClick = (location: ReplyLocationsEnum) =>
    this.withConfirmReplaceDraft(({existingDraftStrategy}) =>
      this.props.onReplyClick(location, existingDraftStrategy),
    );

  private onReplyAllClick = (location: ReplyLocationsEnum) =>
    this.withConfirmReplaceDraft(({existingDraftStrategy}) =>
      this.props.onReplyAllClick(location, existingDraftStrategy),
    );

  private onForwardClick = (location: ReplyLocationsEnum, shouldCreateConversation: boolean) =>
    this.withConfirmReplaceDraft(({existingDraftStrategy}) =>
      this.props.onForwardClick(location, shouldCreateConversation, existingDraftStrategy),
    );

  private visualTimelineVariation = () => {
    const {message, timelineVariation} = this.props;

    // Read-only drafts should always be rendered using MessageChatViewer.
    if (
      timelineVariation === TimelineVariationsEnum.SYNCHRONOUS &&
      message.visualStatus === MessageVisualStatusesEnum.DRAFT &&
      message.status === MessageStatusesEnum.DRAFT_COMPOSE
    ) {
      return TimelineVariationsEnum.ASYNCHRONOUS;
    }

    return timelineVariation;
  };

  private onAnnotationAnimationEnd = (e: Event) => {
    const {target} = e;
    if (!target) {
      return;
    }

    target.removeEventListener('animationend', this.onAnnotationAnimationEnd);
    if (!isElement(target)) {
      return;
    }

    target.classList.remove('front_annotation_focused');
  };

  componentDidUpdate(prevProps: MessageViewerProps) {
    const jumpKeyChanged = prevProps.jumpKey !== this.props.jumpKey;
    const jumpSourceActivityIdChanged = prevProps.jumpSourceActivityId !== this.props.jumpSourceActivityId;
    const shouldJump =
      this.props.jumpSourceActivityId !== undefined && (jumpKeyChanged || jumpSourceActivityIdChanged);

    if (!shouldJump) {
      return;
    }

    const messageElement = this.messageBaseRef.current;

    if (!messageElement) {
      return;
    }

    messageElement
      .querySelector(`[${sourceActivityIdAttribute}="${this.props.jumpSourceActivityId}"]`)
      ?.scrollIntoView();

    messageElement
      .querySelectorAll(`[${sourceActivityIdAttribute}="${this.props.jumpSourceActivityId}"]`)
      .forEach((el) => {
        el.classList.add('front_annotation_focused');
        el.addEventListener('animationend', this.onAnnotationAnimationEnd);
      });
  }

  render() {
    const {timelineVariation, message} = this.props;

    if (this.visualTimelineVariation() === TimelineVariationsEnum.SYNCHRONOUS) {
      return this.renderSynchronous();
    }

    // Synchronous shared drafts do not have inline images included.
    if (
      this.visualTimelineVariation() === TimelineVariationsEnum.ASYNCHRONOUS &&
      timelineVariation === TimelineVariationsEnum.SYNCHRONOUS &&
      message.visualStatus === MessageVisualStatusesEnum.DRAFT
    ) {
      const bodyWithoutImages = removeChatBodyInlineImages(message.body);

      const messageWithoutBodyImages = {...message, body: bodyWithoutImages};
      return this.renderSingleMessage(messageWithoutBodyImages);
    }

    // Standard behavior, render a single message for ASYNCHRONOUS channels with attachments inline.
    return this.renderSingleMessage(message);
  }

  /**
   * When rendering the synchronous timeline we break messages with attachments into multiple "dummy" messages.
   * The first dummy message is used for the message body, unless the body is empty in which case we render nothing.
   * The remaining dummy messages are rendered for each existing message attachment.
   */
  private renderSynchronous() {
    const {message, isChatFollowUp} = this.props;

    // 1. Cannot do SYNCHRONOUS attachment handling unless message is loaded (TIMELINE).
    // 2. No reason to do SYNCHRONOUS attachment handling if there are no attachments.
    if (message.visualStatus !== MessageVisualStatusesEnum.TIMELINE || message.attachments.length === 0) {
      return this.renderSingleMessage(message);
    }

    // For SYNCHRONOUS channels, we display the text as its own message and then one message per-attachment
    const messageBody = message.body ?? '';

    const bodyWithoutAttachments = removeChatBodyInlineImages(messageBody);
    const hasText = htmlToText(bodyWithoutAttachments).length > 0;

    const hasInlineAttachments = message.attachments.some((a) => a.isInline);
    const renderMessageBody = () => {
      if (hasInlineAttachments) {
        return messageBody;
      }

      if (hasText) {
        return bodyWithoutAttachments;
      }

      return emptyCleanHtml;
    };

    const messageAndAttachments = this.renderMessageViewerBase(
      {
        ...message,
        body: renderMessageBody(),
      },
      isChatFollowUp,
    );

    return (
      <>
        {this.maybeRenderCalendarInviteViewer()}
        {this.maybeRenderIntegrationInformationBanner()}

        {messageAndAttachments}

        {this.maybeRenderResnoozeNotification()}
      </>
    );
  }

  private renderSingleMessage(message: MessageModel) {
    return (
      <>
        {this.maybeRenderCalendarInviteViewer()}
        {this.maybeRenderIntegrationInformationBanner()}
        {this.renderMessageViewerBase(message, this.props.isChatFollowUp)}
        {this.maybeRenderResnoozeNotification()}
        <ViewerReplaceDraftDialog
          onConfirmReplaceDraft={this.state.onConfirmReplaceDraft}
          onRequestClose={() => this.setState({onConfirmReplaceDraft: undefined})}
        />
      </>
    );
  }

  private maybeRenderBanner(isRounded: boolean) {
    const {panelId, message, author, isUserAuthor, isExpanded, aiTagMarks} = this.props;
    const {isExternalContentHidden} = this.state;

    // Check if we have blocked some external content.
    if (isExternalContentHidden && isExpanded && messageBodyHasExternalContent(message)) {
      return (
        <HiddenContentMessageBanner
          onShow={() => this.setState({isExternalContentHidden: false})}
          isRounded={isRounded}
        />
      );
    }

    if (message.hasMissingBody && message.source === MessageSourcesEnum.GMAIL) {
      return <MissingBodyMessageBanner message={message} isRounded={isRounded} />;
    }

    if (
      messageViewerPropsHasConversation(this.props) &&
      aiTagMarks &&
      aiTagMarks.length > 0 &&
      Boolean(this.props.isReviewingAiTags)
    ) {
      const harvestingAiMark = aiTagMarks.find((mark) => mark.model === HARVESTING_MODEL);
      const aiMarkToRender = harvestingAiMark;

      if (!aiMarkToRender) {
        return null;
      }

      return (
        <AiTagSuggestionBanner
          panelId={RViewerPanelId.is(panelId) ? panelId : undefined}
          conversationId={this.props.conversationId}
          aiMark={aiMarkToRender}
          isRounded={isRounded}
        />
      );
    }

    if (
      message.status === MessageStatusesEnum.IMPORTED &&
      message.visualStatus === MessageVisualStatusesEnum.TIMELINE &&
      message.error
    ) {
      return <ImportMessageFailedBanner error={message.error} isRounded={isRounded} />;
    }

    // All other banners are applied to draft messages only.
    if (message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
      return null;
    }

    // Don't show banner for sending drafts in synchronous channels
    if (
      message.status === MessageStatusesEnum.DRAFT_SENDING &&
      this.props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS
    ) {
      return null;
    }

    if (
      message.status === MessageStatusesEnum.DRAFT_SENDING ||
      message.status === MessageStatusesEnum.DRAFT_LATER
    ) {
      return (
        <LaterMessageBanner
          isUserAuthor={isUserAuthor}
          message={message}
          author={author}
          onCancelSendClick={this.props.onCancelSendClick}
          isRounded={isRounded}
        />
      );
    }

    if (message.status === MessageStatusesEnum.DRAFT_FAILED) {
      return (
        <FailedMessageBanner
          onRetryFailedDraftClick={this.props.onRetryFailedDraftClick}
          onDeleteDraftClick={this.props.onDeleteDraftClick}
          message={message}
          isRounded={isRounded}
        />
      );
    }

    if (message.status === MessageStatusesEnum.DRAFT_COMPOSE && isUserAuthor && RViewerPanelId.is(panelId)) {
      return (
        <EditHereMessageBannerContainer messageUid={message.uid} panelId={panelId} isRounded={isRounded} />
      );
    }

    if (message.status === MessageStatusesEnum.DRAFT_COMPOSE && this.props.isMessageShared && author) {
      return (
        <SharedDraftMessageBannerContainer
          isAuthorReplying={this.props.isAuthorReplying}
          onStealDraftClick={this.props.onStealDraftClick}
          panelId={this.props.panelId}
          author={author}
          message={message}
          lastUpdatedAt={message.updatedAt}
          isRounded={isRounded}
          isComposerPopup={this.props.isInMessagePopup}
        />
      );
    }

    if (message.status === MessageStatusesEnum.DRAFT_PENDING) {
      return (
        <PendingDraftMessageBanner
          message={message}
          onCancelSendClick={this.props.onCancelSendClick}
          isRounded={isRounded}
        />
      );
    }

    return null;
  }

  private maybeRenderCalendarInviteViewer() {
    // We render the calendar invite viewer once for every series
    // of messages that reference the same calendar event.
    const {namespace, message, containsNewCalendarEvent, panelId} = this.props;
    const calendarEventUid = message.calendarEventUids[0];
    if (!calendarEventUid || !containsNewCalendarEvent) {
      return null;
    }

    return (
      <CalendarInviteViewerContainer
        namespace={namespace}
        message={message}
        calendarEventUid={calendarEventUid}
        panelId={panelId}
      />
    );
  }

  private maybeRenderIntegrationInformationBanner() {
    const {isFirstMessage, relatedIntegration, isUserVisitor} = this.props;
    if (!isFirstMessage || !relatedIntegration || isUserVisitor) {
      return null;
    }

    trackInterfaceEvent(InterfaceEventsEnum.INTEGRATION_INFORMATION_BANNER_ACTION, {
      integration: relatedIntegration.id,
      action: 'displayed',
    });
    return <IntegrationInformationBannerContainer integration={relatedIntegration} />;
  }

  private maybeRenderResnoozeNotification() {
    if (!messageViewerPropsHasConversation(this.props)) {
      return null;
    }

    const {canceledReminder, cellsContext, conversationId, conversationActionContext} = this.props;

    return (
      <BasicViewerResnoozeContainer
        canceledReminder={canceledReminder}
        cellsContext={cellsContext}
        conversationId={conversationId}
        conversationActionContext={conversationActionContext}
      />
    );
  }

  private renderMessageViewerBase(message: MessageModel, isChatFollowUp: boolean, isImageMessage?: boolean) {
    const {
      isExpanded,
      isFirstActivity,
      isPreviousActivityMessage,
      isInMessagePopup,
      isInMessagePopout,
      containsNewCalendarEvent,
      relatedIntegration,
      isInbound,
    } = this.props;
    const onKeyboardAction: KeyboardActionEventHandler = (e) => {
      // Check if the messageBase has a selection
      const hasSelection = containsSelection(this.messageBaseRef.current);

      // If we have no selection, nothing to do.
      if (!hasSelection) {
        return;
      }

      // Otherwise, block the event.
      e.preventDefault();
    };

    const keyHandlers = {
      [KeyboardActionsEnum.TRASH]: onKeyboardAction,
    };

    const calendarEventUid = message.calendarEventUids[0];
    const isFirst =
      isFirstActivity && (!calendarEventUid || !containsNewCalendarEvent) && !relatedIntegration;

    if (this.visualTimelineVariation() === TimelineVariationsEnum.SYNCHRONOUS) {
      return this.renderSynchronousMessageViewer(message, isImageMessage);
    }

    return (
      <MessageViewerBase
        isSameTypeAsPrevious={isPreviousActivityMessage}
        isChatFollowUp={isChatFollowUp}
        isPopup={isInMessagePopup}
        ref={this.messageBaseRef}
        interactionId={message.uid}
        isFirst={isFirst}
        timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
        activityId={this.props.activityId}
        role="article"
      >
        <MessageBase
          renderBanner={({isRounded}) => this.maybeRenderBanner(isRounded)}
          isPopup={isInMessagePopup}
          isPopout={isInMessagePopout}
          isFaded={!isExpanded}
          isImageMessage={isImageMessage}
          timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
          isInbound={isInbound}
          wasJustHighlighted={this.props.wasJustHighlighted}
        >
          <KeyboardShortcuts handlers={keyHandlers} noPreventDefault>
            {this.renderContent(
              {
                ...this.props,
                onPopoutClick: this.onPopoutClick,
                onReplyClick: this.onReplyClick,
                onReplyAllClick: this.onReplyAllClick,
                onForwardClick: this.onForwardClick,
              },
              message,
              isChatFollowUp,
            )}
          </KeyboardShortcuts>
        </MessageBase>
      </MessageViewerBase>
    );
  }

  private renderSynchronousMessageViewer(message: MessageModel, isImageMessage?: boolean) {
    // TODO: Reduce duplication with renderMessageViewerBase().
    const calendarEventUid = message.calendarEventUids[0];
    const isFirst =
      this.props.isFirstActivity &&
      (!calendarEventUid || !this.props.containsNewCalendarEvent) &&
      !this.props.relatedIntegration;

    return (
      <MessageViewerBase
        isSameTypeAsPrevious={this.props.isPreviousActivityMessage}
        isChatFollowUp={this.props.isChatFollowUp}
        isPopup={this.props.isInMessagePopup}
        ref={this.messageBaseRef}
        interactionId={message.uid}
        isFirst={isFirst}
        timelineVariation={TimelineVariationsEnum.SYNCHRONOUS}
        activityId={this.props.activityId}
      >
        <MessageViewerSynchronous
          renderBanner={({isRounded}) => this.maybeRenderBanner(isRounded)}
          message={message}
          panelId={this.props.panelId}
          author={this.props.author}
          isChatUserOnline={Boolean(this.props.isChatUserOnline)}
          isChatFollowUp={Boolean(this.props.isChatFollowUp)}
          visualRecipients={this.props.visualRecipients}
          isImageMessage={Boolean(isImageMessage)}
          hasMessageAnnotations={this.props.hasMessageAnnotations}
          annotations={this.props.annotations}
          onCreateAnnotatedComment={this.props.onCreateAnnotatedComment}
          cellsContext={this.props.cellsContext}
          activitySourceVisualType={this.props.activitySourceVisualType}
          widgetCompanyName={
            (messageViewerPropsHasConversation(this.props) && this.props.widgetCompanyName) || undefined
          }
          isMessageViewerExpanded={this.props.isExpanded}
          scrollerVisibilityRef={this.props.scrollerVisibilityRef}
          isNewUnread={this.props.isNewUnread}
          isUserAuthor={this.props.isUserAuthor}
          namespace={this.props.namespace}
          activityId={this.props.activityId}
          isOnlyMessage={this.props.isOnlyMessage}
          isOnlyActivity={this.props.isOnlyActivity}
          isFirstMessage={this.props.isFirstMessage}
          onReplyClick={this.onReplyClick}
          onReplyAllClick={this.onReplyAllClick}
          onForwardClick={this.onForwardClick}
          onCloneClick={this.props.onCloneClick}
          onDestroyConversationClick={this.props.onDestroyConversationClick}
          onPopoutClick={this.onPopoutClick}
          needsSeenPill={this.props.message.uid === this.props.lastSeenMessageUid}
        />
      </MessageViewerBase>
    );
  }

  private renderContent(props: RenderContentProps, message: MessageModel, isChatFollowUp: boolean) {
    // If this message is collapsed, render that state.
    const {isExpanded, canHideExternalContent} = props;
    const {isExternalContentHidden} = this.state;

    if (!isExpanded || message.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
      return (
        <MessageViewerCollapsed
          {...props}
          isMessageViewerExpanded={props.isExpanded}
          searchQuery={props.cellsContext.searchQuery}
        />
      );
    }

    switch (message.visualType) {
      case MessageVisualTypesEnum.EMAIL: {
        const canHide = canHideExternalContent && isExternalContentHidden;
        const isExternalContentCurrentlyHidden = canHide && messageBodyHasExternalContent(message);
        return (
          <MessageViewerEmail
            {...props}
            onReplyClick={this.onReplyClick}
            onReplyAllClick={this.onReplyAllClick}
            onForwardClick={this.onForwardClick}
            message={message}
            isExternalContentHidden={isExternalContentCurrentlyHidden}
            isMessageViewerExpanded={props.isExpanded}
            hasMessageTranslation={props.hasMessageTranslation}
          />
        );
      }

      case MessageVisualTypesEnum.TWEET:
        return (
          <MessageViewerTweetContainer
            {...props}
            onReplyClick={this.onReplyClick}
            onReplyAllClick={this.onReplyAllClick}
            onForwardClick={this.onForwardClick}
            message={message}
            isMessageViewerExpanded={props.isExpanded}
          />
        );

      default:
        return (
          <MessageViewerChat
            {...props}
            onReplyClick={this.onReplyClick}
            onReplyAllClick={this.onReplyAllClick}
            onForwardClick={this.onForwardClick}
            message={message}
            isMessageViewerExpanded={props.isExpanded}
            isChatFollowUp={isChatFollowUp}
          />
        );
    }
  }
}

/*
 * Helpers.
 */

function removeChatBodyInlineImages(body: CleanHtml): CleanHtml {
  return sanitize(body.replaceAll(bodyImgRE, ''));
}
